import { useMemo } from 'react';
import useDeleteClip from './useDeleteClip';
import useDeleteClips from './useDeleteClips';
import usePendingClips from './usePendingClips';
import useRenameClip from './useRenameClip';
import useUpdateClips from './useUpdateClips';
import useUpdateClipOrdering from './useUpdateClipOrdering';
import useUpdateClipProject from './useUpdateClipProject';
import {
  StudioClipsDataAndActions,
  usePersistentClipStateAndActions,
} from './common';
import useProjectClipsQuery from './useProjectClipsQuery';

const useStudioClips = (projectId: string): StudioClipsDataAndActions => {
  const {
    selectedClipIds,
    setSelectedClipIds,
    previouslySelectedClip,
    setPreviouslySelectedClip,
    lastMovedClipDestinationProjectId,
    lastCopiedClipDestinationProjectId,
    lastUsedCombineClipMsGapDuration,
    setLastUsedCombineClipMsGapDuration,
    setLastMovedClipDestinationProjectId,
    setLastCopiedClipDestinationProjectId,
  } = usePersistentClipStateAndActions();

  const deleteClip = useDeleteClip();
  const deleteClips = useDeleteClips();
  const renameClip = useRenameClip();
  const updateClips = useUpdateClips();
  const updateClipOrdering = useUpdateClipOrdering();
  const updateClipProject = useUpdateClipProject();

  const { addPendingClip, updatePendingClip, deletePendingClip } =
    usePendingClips(projectId);

  const {
    clips,
    loading,
    error,
    networkStatus,
    fetchMore,
    hasNextPage,
    variables,
  } = useProjectClipsQuery(projectId);

  return useMemo(
    () => [
      {
        clips,
        loading: loading || networkStatus < 7,
        error,
        selectedClipIds,
        previouslySelectedClip,
        lastMovedClipDestinationProjectId,
        lastCopiedClipDestinationProjectId,
        lastUsedCombineClipMsGapDuration,
        hasNextPage,
        variables,
      },
      {
        fetchMore,
        deleteClip,
        deleteClips,
        addPendingClip,
        updatePendingClip,
        deletePendingClip,
        renameClip,
        updateClips,
        setSelectedClipIds,
        setPreviouslySelectedClip,
        updateClipOrdering,
        setLastMovedClipDestinationProjectId,
        setLastCopiedClipDestinationProjectId,
        setLastUsedCombineClipMsGapDuration,
        updateClipProject,
        hasNextPage,
      },
    ],
    [
      clips,
      loading,
      networkStatus,
      error,
      selectedClipIds,
      previouslySelectedClip,
      lastMovedClipDestinationProjectId,
      lastCopiedClipDestinationProjectId,
      lastUsedCombineClipMsGapDuration,
      deletePendingClip,
      hasNextPage,
      fetchMore,
      deleteClip,
      deleteClips,
      addPendingClip,
      updatePendingClip,
      renameClip,
      updateClips,
      setSelectedClipIds,
      setPreviouslySelectedClip,
      updateClipOrdering,
      setLastMovedClipDestinationProjectId,
      setLastCopiedClipDestinationProjectId,
      setLastUsedCombineClipMsGapDuration,
      updateClipProject,
      variables,
    ]
  );
};

export default useStudioClips;
